import { Suspense, lazy } from 'react';
import { Redirect, Switch } from 'react-router-dom';
import routes from '~/routes';
import { GlobalInterpretationsPage } from 'pages/interpretations/page';
import { DocumentEntrypointPage } from 'pages/documents/router/page';
import { TitleWorkspaces } from 'pages/title-workspaces';
import { TitleWorkspaceEntrypointPage } from 'pages/title-workspaces/router/page';
import { WorkspaceShowPage } from 'pages/interest-certification/workspaces/show/page';
import { EntityListPage } from 'pages/entities/list/page';
import { NewEntityPage } from 'pages/entities/new/page';
import { NewDocumentPage } from 'pages/documents/new/page';
import { ShowDevelopmentAreaPage } from 'pages/development-areas/show/page';
import { DevelopmentAreasPage } from 'pages/development-areas/page';
import { ShowWellPage } from 'pages/wells/show/page';
import { WellWorkspacePage } from 'pages/well-workspaces/page';
import { TitleSupportWorkspacesPage } from 'pages/title-support-workspaces/page';
import { TitleSupportWorkspacePage } from 'pages/title-support-workspaces/show/page';
import { NewTitleSupportWorkspacePage } from 'pages/title-support-workspaces/new/page';
import { DocumentSearchPage } from 'pages/documents/search/page';
import { WellSearchPage } from 'pages/wells/search/page';
import { DevelopmentAreaSummaryMapPage } from 'pages/development-areas/summary-map/page';
import { ReferenceLocationsMapPage } from 'pages/reference-locations/map/page';
import { ReferenceLocationsShowPage } from 'pages/reference-locations/show/page';
import { ActivitiesPage } from 'pages/activities/page';
import { TitleChainCalculatorPage } from 'pages/title-chain-calculator/page';
import { Route } from 'components/route';
import { ShowPackagePage } from 'pages/packages/show/page';
import { Eval3NewPage } from 'pages/packages/show/eval3-new/page';
import {
  Eval3LandingResolver,
  PackageEval3Redirect,
} from 'pages/packages/show/eval3-new/eval3-landing-resolver';
import { ScenariosPage } from 'pages/scenarios/list/page';
import { NewScenarioPage } from 'pages/scenarios/new/page';
import { EditScenarioPage } from 'pages/scenarios/show/page';
import { ComparisonToolPage } from 'pages/packages/compare/result/page';
import { CompareEvaluationsPage } from 'pages/packages/compare/wizard/page';
import {
  AnalyzePage,
  AnalyzeFactorOrderPage,
  AnalyzeResultsPage,
} from 'pages/packages/compare/analyze/content';
import { ListPackagesPage } from 'pages/packages/list/page';
import { DivisionOrdersListPage } from 'pages/division-orders/list/page';
import { DivisionOrderShowPage } from 'pages/division-orders/show/page';
import { DivisionOrderLineItemShowPage } from 'pages/division-orders/line-items/show/page';
import { RoyaltyChecksListPage } from 'pages/royalty-checks/list/page';
import { RoyaltyCheckShowPage } from 'pages/royalty-checks/show/page';
import { PackageWizardPage } from 'pages/packages/new/page';
import { EntityPageEntryPoint } from 'pages/entities/router/page';
import { EnergyLinkAccountsPage } from 'pages/energy-link/accounts/energy-link-accounts-page';
import { EnergyLinkAccountsNewPage } from 'pages/energy-link/accounts/new-accounts/energy-link-accounts-new-page';
import { EnergyLinkPage } from 'pages/energy-link';
import StructureGridsManagementPage from 'pages/structure-grids/structure-grids-management/structure-grids-management';
import GeologicIntervalTypeCurvePage from 'pages/type-curve-formations/type-curve-formations';
import { FormationManagementPage } from 'pages/formation-management/page';
import { TypeCurvesListingPage } from 'pages/type-curves/type-curves-listing';
import AddStructureGridPage from 'pages/structure-grids/structure-grids-management/add-structure-grid/add-structure-grid';
import GeologicalMap3D from 'pages/structure-grids/structure-grids-management/geological-map/geological-map';
import { StateTaxesPage } from 'pages/state-taxes/state-taxes';
import { ListAreasPage } from 'pages/area-geometries/list/page';
import ShowAreaGeometryPage from 'pages/area-geometries/show/page';
import { AddStructureGridsCollection } from 'pages/structure-grids/structure-grids-collection/add-structure-grids-collection';
import StructureGridCollectionManagement from 'pages/structure-grids/structure-grids-collection/structure-grid-collection-management';
import { StructureGridsCollectionDetails } from 'pages/structure-grids/structure-grids-collection/structure-grids-collection-details';
import TypeCurveDetailsPage from 'pages/type-curves/type-curves-details-page';
import CrmTractsPage from 'pages/crm-tracts-subtracts/page';
import { TractWizard } from 'pages/crm-tracts-subtracts/tract-wizard';
import BulkUploadPage from 'pages/crm-tracts-subtracts/bulk-upload/page';
import BatchResultsPage from 'pages/crm-tracts-subtracts/bulk-upload/results-page';
import { TitleChainReconcilerPage } from 'pages/title-chain-reconciler/page';
import { TitleValidatorsPage } from 'pages/title-validators/page';
import { AfeCatalogPage } from 'pages/afe-catalog/page';
import { AfeWizardPage } from 'pages/afe-catalog/new/page';
import { AfeCatalogDetailPage } from 'pages/afe-catalog/detail/page';
import { CountyCoveragePage } from 'pages/county-coverage/page';
import { DocumentViewerPage } from 'pages/document-viewer/page';
import { DuplicateNriCalculationsPage } from 'pages/admin/duplicate-nri-calculations/page';
import { DuplicateParametersPage } from 'pages/admin/duplicate-parameters/page';
import { DuplicateGeometriesPage } from 'pages/admin/duplicate-geometries/page';
import { NriDiagnosticsPage } from 'pages/admin/nri-diagnostics/page';
import { ScheduledJobsPage } from 'pages/admin/scheduled-jobs/page';
import { IntersectionCoordinationQueuePage } from 'pages/admin/intersection-coordination-queue/page';
import { SidekiqTimedQueuesPage } from 'pages/admin/sidekiq-timed-queues/page';
import { AdminDashboardPage } from 'pages/admin/dashboard/page';
import { BlobDrainStatusPage } from 'pages/admin/blob-drain-status/page';
import { BulkDumpStatusPage } from 'pages/admin/bulk-dump-status/page';
import { EvaluationOutputPage } from 'pages/admin/development-area-evaluation-output/page';
import { Eval2RunsStatusPage } from 'pages/admin/eval2-runs-status/page';
import { LegacyUnitEvaluationDownloadsPage } from 'pages/admin/legacy-unit-evaluation-downloads/page';
import { AgentCostsPage } from 'pages/admin/agent-costs/page';
import { AgentCostDetailPage } from 'pages/admin/agent-costs/agent-detail/page';
import { WellSpacingJobStatusPage } from 'pages/admin/well-spacing-job-status/page';
import { InventoryAutomationStatusPage } from 'pages/admin/inventory-automation-status/page';
import { UnitTypeMismatchesQueryPage } from 'pages/admin/unit-type-mismatches-query/page';
import { MissingWellAssignmentsPage } from 'pages/admin/missing-well-assignments/page';
import { NonIntersectingExplicitAssignmentsPage } from 'pages/admin/non-intersecting-explicit-assignments/page';
import { UnitWellAssignmentsPage } from 'pages/admin/unit-well-assignments/page';
import { AllocationManagerChecksPage } from 'pages/admin/allocation-manager-checks/page';
import { SidekiqQueueAssignmentsPage } from 'pages/admin/sidekiq-queue-assignments/page';
import { EmptyAllocationManagersQueryPage } from 'pages/admin/empty-allocation-managers-query/page';
import { MissingSubtractBosPage } from 'pages/admin/missing-subtract-bos/page';
import { FoucsDiagnosisPage } from 'pages/admin/foucs-diagnosis/page';
import { UatsWithoutAmlisPage } from 'pages/admin/uats-without-amlis/page';
import { OverAllocatedSlotsPage } from 'pages/admin/over-allocated-slots/page';
import { SubtractAcreageDriftPage } from 'pages/admin/subtract-acreage-drift/page';
import { SubtractsMissingTirsPage } from 'pages/admin/subtracts-missing-tirs/page';
import { OrphanedSubtractGeometriesPage } from 'pages/admin/orphaned-subtract-geometries/page';
import { StaleRecordsAuditPage } from 'pages/admin/stale-records-audit/page';
import { BulkDedupePage } from 'pages/admin/bulk-dedupe/page';
import { BulkDedupeRunPage } from 'pages/admin/bulk-dedupe/run-detail/page';
import { BosDriftAuditPage } from 'pages/admin/bos-drift-audit/page';
import { ProvisioningPage } from 'pages/admin/provisioning/page';
import { BatchDetailPage } from 'pages/admin/provisioning/batch-detail/page';
import { SubmissionDetailPage } from 'pages/admin/provisioning/submission-detail/page';
import { PromptManagementPage } from 'pages/admin/prompt-management/page';
import { AutoMatchConfigurationPage } from 'pages/admin/auto-match/page';
import { EntityCleanupLogPage } from 'pages/admin/entity-cleanup-log/page';
import { DevelopmentPaceScenarioFormPage } from 'pages/development-pace-scenarios/scenario-form/page';
import { NewDcaScenarioPage } from 'pages/dca-scenarios/new/page';
import { EditDcaScenarioPage } from 'pages/dca-scenarios/edit/page';
import { DcaScenariosListPage } from 'pages/dca-scenarios-list';
import { DcaWorkspacePage } from 'pages/dca-workspace/page';
import { TypeCurveWorkspacePage } from 'pages/type-curve-workspace/page';
import { InventoryWorkspacePage } from 'pages/inventory-workspace/page';
import { MapQueryPage } from 'pages/map-query/page';
import { ForwardPriceCurvePage } from 'pages/forward-price-curve/page';
import { CurveManagementPage } from 'pages/pricing/curve-management/page';
import { ContractBuilderPage } from 'pages/pricing/contract-builder/page';
import { TransformationBuilderPage } from 'pages/pricing/transformation-builder/page';
import { MarketDataPage } from 'pages/pricing/market-data/page';
import { PriceDecksPage } from 'pages/price-decks/page';
import { DevelopmentPaceScenariosPage } from 'pages/development-pace-scenarios/page';
import { NewTypeCurveScenarioPage } from 'pages/type-curve-scenarios/new/page';
import { EditTypeCurveScenarioPage } from 'pages/type-curve-scenarios/edit/page';
import { DiscountRateScenariosPage } from 'pages/discount-rate-scenarios/page';
import { DiscountRateScenarioFormPage } from 'pages/discount-rate-scenarios/scenario-form/page';
import { DifferentialScenariosPage } from 'pages/differential-scenarios/list/page';
import { DifferentialScenarioFormPage } from 'pages/differential-scenarios/scenario-form/page';
import { TaxScenariosPage } from 'pages/tax-scenarios/page';
import { TaxScenarioFormPage } from 'pages/tax-scenarios/scenario-form/page';
import { EvaluationCasesPage } from 'pages/evaluation-cases/page';
import { CaseEditorPage } from 'pages/evaluation-cases/case-editor/page';
import { ExpenseScenariosPage } from 'pages/expense-scenarios/page';
import { ExpenseScenarioFormPage } from 'pages/expense-scenarios/scenario-form/page';
import { PriceDeckFormPage } from 'pages/price-decks/new/page';
import { ShowPriceDeckPage } from 'pages/price-decks/show/page';

// Insights Lexicon is the app's only consumer of the rich-text editor (Tiptap +
// KaTeX, ~77 KB gzipped just for KaTeX). Lazy-loading these routes keeps that
// dependency out of the core bundle — it loads only when a Lexicon route opens.
const InsightsLexiconPage = lazy(() =>
  import('pages/insights-lexicon').then((m) => ({
    default: m.InsightsLexiconPage,
  }))
);
const InsightsLexiconTermFormPage = lazy(() =>
  import('pages/insights-lexicon').then((m) => ({
    default: m.InsightsLexiconTermFormPage,
  }))
);
const InsightsLexiconDetailPage = lazy(() =>
  import('pages/insights-lexicon').then((m) => ({
    default: m.InsightsLexiconDetailPage,
  }))
);
// Lazy-loaded for the same reason as the Lexicon routes above: this page is
// reachable from anywhere but opened rarely, so it should not sit in the core
// bundle that every other page pays for.
const BugsFeaturesPage = lazy(() =>
  import('pages/bugs-features').then((m) => ({
    default: m.BugsFeaturesPage,
  }))
);
const BugsFeatureDetailPage = lazy(() =>
  import('pages/bugs-features').then((m) => ({
    default: m.BugsFeatureDetailPage,
  }))
);
// The bell in the header is the usual way in; this page is the overflow for when 20 rows
// are not enough, so it is lazy for the same reason as the routes above.
const NotificationsPage = lazy(() =>
  import('pages/notifications').then((m) => ({
    default: m.NotificationsPage,
  }))
);

export function Router() {
  return (
    <Switch>
      <Route exact path={routes.rootPath}>
        <DevelopmentAreaSummaryMapPage />
      </Route>
      <Route exact path={routes.documentsPath}>
        <DocumentSearchPage />
      </Route>
      <Route exact path={routes.interpretationsPath}>
        <GlobalInterpretationsPage />
      </Route>
      <Route exact path={routes.newDocumentsPath}>
        <NewDocumentPage />
      </Route>
      {/* One route for a document and all of its tabs. Matching every entry
          URL here keeps the entrypoint mounted across a tab switch, so the
          header stays put and the document isn't refetched. */}
      <Route
        path={[
          routes.courthouseDocumentPath,
          routes.nonCourthouseDocumentPath,
          routes.regulatoryDocumentPath,
          routes.afeDocumentPath,
          routes.divisionOrderDocumentPath,
          routes.royaltyCheckDocumentPath,
          routes.documentInterpretationsPath,
        ]}
      >
        <DocumentEntrypointPage />
      </Route>
      <Route exact path={routes.activitiesPath}>
        <ActivitiesPage />
      </Route>
      <Route exact path={routes.wellsPath}>
        <WellSearchPage />
      </Route>
      <Route exact path={routes.wellPath}>
        <ShowWellPage />
      </Route>
      <Route exact path={routes.wellWorkspacePath}>
        <WellWorkspacePage />
      </Route>
      <Route exact path={routes.developmentAreasPath}>
        <DevelopmentAreasPage />
      </Route>
      <Route path={routes.developmentAreaPath}>
        <ShowDevelopmentAreaPage />
      </Route>
      <Route exact path={routes.referenceLocationsPath}>
        <ReferenceLocationsMapPage />
      </Route>
      <Route exact path={routes.referenceLocationPath}>
        <ReferenceLocationsShowPage />
      </Route>
      <Route exact path={routes.titleWorkspacesPath}>
        <TitleWorkspaces />
      </Route>
      <Route path={routes.titleWorkspacePath}>
        <TitleWorkspaceEntrypointPage />
      </Route>
      <Route exact path={routes.titleSupportWorkspacesPath}>
        <TitleSupportWorkspacesPage />
      </Route>
      <Route exact path={routes.titleSupportWorkspaceNewPath}>
        <NewTitleSupportWorkspacePage />
      </Route>
      <Route exact path={routes.titleSupportWorkspacePath}>
        <TitleSupportWorkspacePage />
      </Route>
      <Route exact path={routes.interestCertificationWorkspacePath}>
        <WorkspaceShowPage />
      </Route>
      <Route exact path={routes.titleChainCalculatorPath}>
        <TitleChainCalculatorPage />
      </Route>
      <Route exact path={routes.titleValidatorsPath}>
        <TitleValidatorsPage />
      </Route>
      <Route exact path={routes.newEntityPath}>
        <NewEntityPage />
      </Route>
      <Route exact path={routes.entitiesPath}>
        <EntityListPage />
      </Route>
      <Route path={routes.entityPath}>
        <EntityPageEntryPoint />
      </Route>
      <Route exact path={routes.compareResultPath}>
        <ComparisonToolPage />
      </Route>
      <Route exact path={routes.compareWizardPath}>
        <CompareEvaluationsPage />
      </Route>
      <Route exact path={routes.compareAnalyzeFactorOrderPath}>
        <AnalyzeFactorOrderPage />
      </Route>
      <Route exact path={routes.compareAnalyzeResultsPath}>
        <AnalyzeResultsPage />
      </Route>
      <Route exact path={routes.compareAnalyzePath}>
        <AnalyzePage />
      </Route>
      <Route exact path={routes.packagesPath}>
        <ListPackagesPage />
      </Route>
      <Route exact path={routes.divisionOrdersPath}>
        <DivisionOrdersListPage />
      </Route>
      <Route exact path={routes.divisionOrderPath}>
        <DivisionOrderShowPage />
      </Route>
      <Route exact path={routes.divisionOrderLineItemPath}>
        <DivisionOrderLineItemShowPage />
      </Route>
      <Route exact path={routes.royaltyChecksPath}>
        <RoyaltyChecksListPage />
      </Route>
      <Route exact path={routes.royaltyCheckPath}>
        <RoyaltyCheckShowPage />
      </Route>
      <Route exact path={routes.newPackagePath}>
        <PackageWizardPage />
      </Route>
      <Route exact path={routes.editPackagePath}>
        <PackageWizardPage />
      </Route>
      {/* Nested eval3-new views, most-specific path first: an eval lives under its
          workspace (`workspaces/:workspaceId/eval/:evalId`), a bare workspace is the
          management/browse view, and `eval3-new` alone is the landing (auto-selects). */}
      <Route exact path={routes.packageEval3NewEvalPath}>
        <Eval3NewPage />
      </Route>
      <Route exact path={routes.packageEval3NewWorkspacePath}>
        <Eval3NewPage />
      </Route>
      <Route exact path={routes.packageEval3NewSavedEvalsPath}>
        <Eval3NewPage savedEvalsView />
      </Route>
      {/* Bare `/eval3` — resolve the latest eval CLIENT-SIDE (no full-page reload); the
          workspace/eval routes above render the content page on a canonical URL. */}
      <Route exact path={routes.packageEval3NewPath}>
        <Eval3LandingResolver />
      </Route>
      {/* Bare `/packages/:id` → the new Eval 3.0 page is the default landing
          (INS-11484). Client-side redirect to `/eval3` (which then resolves the latest
          eval via Eval3LandingResolver) — no full-page reload. Declared BEFORE the
          non-exact `packagePath` route so ShowPackagePage still serves `/eval3-legacy*`
          and `/evaluations*`. */}
      <Route exact path={routes.packagePath}>
        <PackageEval3Redirect />
      </Route>
      <Route path={routes.packagePath}>
        <ShowPackagePage />
      </Route>
      <Route exact path={routes.newScenarioPath}>
        <NewScenarioPage />
      </Route>
      <Route exact path={routes.scenarioPath}>
        <EditScenarioPage />
      </Route>
      <Route exact path={routes.scenariosPath}>
        <ScenariosPage />
      </Route>
      <Route exact path={routes.energyLinkPath}>
        <EnergyLinkPage />
      </Route>
      <Route exact path={routes.energyLinkAccountsPath}>
        <EnergyLinkAccountsPage />
      </Route>
      <Route exact path={routes.energyLinkNewAccountPath}>
        <EnergyLinkAccountsNewPage />
      </Route>
      <Route exact path={routes.structureGridsPath}>
        <StructureGridsManagementPage />
      </Route>
      <Route exact path={routes.typeCurveFormationsPath}>
        <GeologicIntervalTypeCurvePage />
      </Route>
      <Route exact path={routes.formationManagementPath}>
        <FormationManagementPage />
      </Route>
      <Route exact path={routes.typeCurvesPath}>
        <TypeCurvesListingPage />
      </Route>
      <Route exact path={routes.typeCurvePath}>
        <TypeCurveDetailsPage />
      </Route>
      {/* `/type_curves/:id` is the one details URL a reader keeps. An in-app navigation that
          still carries the header path lands here and is swapped for the canonical one, the same
          way the server 301s a browser that arrives on it. */}
      <Redirect
        exact
        from={routes.typeCurveHeaderDetailsPath}
        to={routes.typeCurvePath}
      />
      <Route exact path={routes.addStructureGridPath}>
        <AddStructureGridPage />
      </Route>
      <Route exact path={routes.structureGridPath}>
        <GeologicalMap3D />
      </Route>
      <Route exact path={routes.addStructureGridCollectionsPath}>
        <AddStructureGridsCollection />
      </Route>
      <Route exact path={routes.structureGridCollectionsPath}>
        <StructureGridCollectionManagement />
      </Route>
      <Route exact path={routes.structureGridCollectionsDetailPath}>
        <StructureGridsCollectionDetails />
      </Route>
      <Route exact path={routes.stateTaxesPath}>
        <StateTaxesPage />
      </Route>
      <Route exact path={routes.areaGeometriesPath}>
        <ListAreasPage />
      </Route>
      <Route exact path={routes.areaGeometryPath}>
        <ShowAreaGeometryPage />
      </Route>
      <Route exact path={routes.crmTractWizardNewPath}>
        <TractWizard />
      </Route>
      <Route exact path={routes.crmTractWizardEditPath}>
        <TractWizard />
      </Route>
      <Route exact path={routes.crmBulkUploadNewPath}>
        <BulkUploadPage />
      </Route>
      <Route exact path={routes.crmBulkUploadPath}>
        <BatchResultsPage />
      </Route>
      <Route exact path={routes.crmTractsAndSubtractsPath}>
        <CrmTractsPage />
      </Route>
      <Route exact path={routes.titleChainReconcilerPath}>
        <TitleChainReconcilerPage />
      </Route>
      <Route exact path={routes.afeCatalogNewPath}>
        <AfeWizardPage />
      </Route>
      <Route exact path={routes.afeCatalogEditPath}>
        <AfeWizardPage />
      </Route>
      <Route exact path={routes.afeCatalogDetailPath}>
        <AfeCatalogDetailPage />
      </Route>
      <Route exact path={routes.afeCatalogPath}>
        <AfeCatalogPage />
      </Route>
      <Route exact path={routes.countyCoveragePath}>
        <CountyCoveragePage />
      </Route>
      <Route exact path={routes.wellSupportingFileDocumentViewerPath}>
        <DocumentViewerPage />
      </Route>
      <Route exact path={routes.adminPath}>
        <AdminDashboardPage />
      </Route>
      <Route exact path={routes.adminDashboardPath}>
        <AdminDashboardPage />
      </Route>
      <Route exact path={routes.adminDuplicateNriCalculationsPath}>
        <DuplicateNriCalculationsPage />
      </Route>
      <Route exact path={routes.adminDuplicateParametersPath}>
        <DuplicateParametersPage />
      </Route>
      <Route exact path={routes.adminDuplicateGeometriesPath}>
        <DuplicateGeometriesPage />
      </Route>
      <Route exact path={routes.adminNriDiagnosticsPath}>
        <NriDiagnosticsPage />
      </Route>
      <Route exact path={routes.adminScheduledJobsPath}>
        <ScheduledJobsPage />
      </Route>
      <Route exact path={routes.adminIntersectionCoordinationQueuePath}>
        <IntersectionCoordinationQueuePage />
      </Route>
      <Route exact path={routes.adminSidekiqTimedQueuesPath}>
        <SidekiqTimedQueuesPage />
      </Route>
      <Route exact path={routes.adminBlobDrainStatusPath}>
        <BlobDrainStatusPage />
      </Route>
      <Route exact path={routes.adminBulkDumpStatusPath}>
        <BulkDumpStatusPage />
      </Route>
      <Route exact path={routes.adminDevelopmentAreaEvaluationOutputPath}>
        <EvaluationOutputPage />
      </Route>
      <Route exact path={routes.adminEval2RunsStatusPath}>
        <Eval2RunsStatusPage />
      </Route>
      <Route exact path={routes.adminLegacyUnitEvaluationDownloadsPath}>
        <LegacyUnitEvaluationDownloadsPage />
      </Route>
      <Route exact path={routes.adminAgentCostDetailPath}>
        <AgentCostDetailPage />
      </Route>
      <Route exact path={routes.adminAgentCostsPath}>
        <AgentCostsPage />
      </Route>
      <Route exact path={routes.adminPromptManagementPath}>
        <PromptManagementPage />
      </Route>
      <Route exact path={routes.adminAutoMatchConfigurationPath}>
        <AutoMatchConfigurationPage />
      </Route>
      <Route exact path={routes.adminEntityCleanupLogPath}>
        <EntityCleanupLogPage />
      </Route>
      <Route exact path={routes.adminWellSpacingJobStatusPath}>
        <WellSpacingJobStatusPage />
      </Route>
      <Route exact path={routes.adminInventoryAutomationStatusPath}>
        <InventoryAutomationStatusPage />
      </Route>
      <Route exact path={routes.adminUnitTypeMismatchesQueryPath}>
        <UnitTypeMismatchesQueryPage />
      </Route>
      <Route exact path={routes.adminMissingWellAssignmentsPath}>
        <MissingWellAssignmentsPage />
      </Route>
      <Route exact path={routes.adminAllocationManagerChecksPath}>
        <AllocationManagerChecksPage />
      </Route>
      <Route exact path={routes.adminSidekiqQueueAssignmentsPath}>
        <SidekiqQueueAssignmentsPage />
      </Route>
      <Route exact path={routes.adminEmptyAllocationManagersQueryPath}>
        <EmptyAllocationManagersQueryPage />
      </Route>
      <Route exact path={routes.adminMissingSubtractBosPath}>
        <MissingSubtractBosPage />
      </Route>
      <Route exact path={routes.adminFoucsDiagnosisPath}>
        <FoucsDiagnosisPage />
      </Route>
      <Route exact path={routes.adminUatsWithoutAmlisPath}>
        <UatsWithoutAmlisPage />
      </Route>
      <Route exact path={routes.adminOverAllocatedSlotsPath}>
        <OverAllocatedSlotsPage />
      </Route>
      <Route exact path={routes.adminSubtractAcreageDriftPath}>
        <SubtractAcreageDriftPage />
      </Route>
      <Route exact path={routes.adminSubtractsMissingTirsPath}>
        <SubtractsMissingTirsPage />
      </Route>
      <Route exact path={routes.adminOrphanedSubtractGeometriesPath}>
        <OrphanedSubtractGeometriesPage />
      </Route>
      <Route exact path={routes.adminStaleRecordsAuditPath}>
        <StaleRecordsAuditPage />
      </Route>
      <Route exact path={routes.adminBulkDedupeRunPath}>
        <BulkDedupeRunPage />
      </Route>
      <Route exact path={routes.adminBulkDedupePath}>
        <BulkDedupePage />
      </Route>
      <Route exact path={routes.adminBosDriftAuditPath}>
        <BosDriftAuditPage />
      </Route>
      <Route exact path={routes.adminNonIntersectingExplicitAssignmentsPath}>
        <NonIntersectingExplicitAssignmentsPage />
      </Route>
      <Route exact path={routes.adminUnitWellAssignmentsPath}>
        <UnitWellAssignmentsPage />
      </Route>
      <Route exact path={routes.adminProvisioningSubmissionPath}>
        <SubmissionDetailPage />
      </Route>
      <Route exact path={routes.adminProvisioningBatchPath}>
        <BatchDetailPage />
      </Route>
      <Route exact path={routes.adminProvisioningPath}>
        <ProvisioningPage />
      </Route>
      <Route exact path={routes.newDevelopmentPaceScenarioPath}>
        <DevelopmentPaceScenarioFormPage />
      </Route>
      <Route exact path={routes.developmentPaceScenarioDetailPath}>
        <DevelopmentPaceScenarioFormPage />
      </Route>
      <Route exact path={routes.dcaScenariosPath}>
        <DcaScenariosListPage />
      </Route>
      <Route exact path={routes.newDcaScenarioPath}>
        <NewDcaScenarioPage />
      </Route>
      <Route exact path={routes.editDcaScenarioPath}>
        <EditDcaScenarioPage />
      </Route>
      <Route exact path={routes.insightsLexiconPath}>
        <Suspense fallback={null}>
          <InsightsLexiconPage />
        </Suspense>
      </Route>
      <Route exact path={routes.insightsLexiconNewPath}>
        <Suspense fallback={null}>
          <InsightsLexiconTermFormPage />
        </Suspense>
      </Route>
      <Route exact path={routes.insightsLexiconEditPath}>
        <Suspense fallback={null}>
          <InsightsLexiconTermFormPage />
        </Suspense>
      </Route>
      <Route exact path={routes.insightsLexiconDetailsPath}>
        <Suspense fallback={null}>
          <InsightsLexiconDetailPage />
        </Suspense>
      </Route>
      <Route exact path={routes.bugsFeaturesPath}>
        <Suspense fallback={null}>
          <BugsFeaturesPage />
        </Suspense>
      </Route>
      {/* Declared after the listing so /feedback does not match :id. */}
      <Route exact path={routes.bugsFeatureDetailPath}>
        <Suspense fallback={null}>
          <BugsFeatureDetailPage />
        </Suspense>
      </Route>
      <Route exact path={routes.notificationsPath}>
        <Suspense fallback={null}>
          <NotificationsPage />
        </Suspense>
      </Route>
      <Route exact path={routes.dcaWorkspacePath}>
        <DcaWorkspacePage />
      </Route>
      <Route exact path={routes.typeCurveWorkspacePath}>
        <TypeCurveWorkspacePage />
      </Route>
      <Route exact path={routes.inventoryWorkspacePath}>
        <InventoryWorkspacePage />
      </Route>
      {/* <Route exact path={routes.dcaScenarioFormPath}>
        <DcaScenarioFormPage />
      </Route> */}
      <Route exact path={routes.mapQueryPath}>
        <MapQueryPage />
      </Route>
      <Route exact path={routes.forwardPriceCurvePath}>
        <ForwardPriceCurvePage />
      </Route>
      <Route
        exact
        path={[
          routes.pricingCurveManagementPath,
          routes.pricingCurveDetailPath,
        ]}
      >
        <CurveManagementPage />
      </Route>
      <Route
        exact
        path={[
          routes.pricingContractBuilderPath,
          routes.pricingContractDetailPath,
        ]}
      >
        <ContractBuilderPage />
      </Route>
      <Route
        exact
        path={[
          routes.pricingTransformationBuilderPath,
          routes.pricingTransformationDetailPath,
        ]}
      >
        <TransformationBuilderPage />
      </Route>
      <Route
        exact
        path={[routes.pricingMarketDataPath, routes.pricingMarketDataViewPath]}
      >
        <MarketDataPage />
      </Route>
      <Route exact path={routes.priceDecksNewPath}>
        <PriceDeckFormPage />
      </Route>
      <Route exact path={routes.priceDeckEditPath}>
        <PriceDeckFormPage />
      </Route>
      <Route exact path={routes.priceDeckDetailPath}>
        <ShowPriceDeckPage />
      </Route>
      <Route exact path={routes.priceDecksPath}>
        <PriceDecksPage />
      </Route>
      <Route exact path={routes.developmentPaceScenariosPath}>
        <DevelopmentPaceScenariosPage />
      </Route>
      <Route exact path={routes.newTypeCurveScenarioPath}>
        <NewTypeCurveScenarioPage />
      </Route>
      <Route exact path={routes.editTypeCurveScenarioPath}>
        <EditTypeCurveScenarioPage />
      </Route>
      <Route exact path={routes.newDiscountRateScenarioPath}>
        <DiscountRateScenarioFormPage />
      </Route>
      <Route exact path={routes.discountRateScenarioDetailPath}>
        <DiscountRateScenarioFormPage />
      </Route>
      <Route exact path={routes.discountRateScenariosPath}>
        <DiscountRateScenariosPage />
      </Route>
      <Route exact path={routes.newDifferentialScenarioPath}>
        <DifferentialScenarioFormPage />
      </Route>
      <Route exact path={routes.differentialScenarioDetailPath}>
        <DifferentialScenarioFormPage />
      </Route>
      <Route exact path={routes.differentialScenariosPath}>
        <DifferentialScenariosPage />
      </Route>
      <Route exact path={routes.newTaxScenarioPath}>
        <TaxScenarioFormPage />
      </Route>
      <Route exact path={routes.taxScenarioDetailPath}>
        <TaxScenarioFormPage />
      </Route>
      <Route exact path={routes.taxScenariosPath}>
        <TaxScenariosPage />
      </Route>
      <Route exact path={routes.newEvaluationCasePath}>
        <CaseEditorPage />
      </Route>
      <Route exact path={routes.evaluationCaseDetailPath}>
        <CaseEditorPage />
      </Route>
      <Route exact path={routes.evaluationCasesPath}>
        <EvaluationCasesPage />
      </Route>
      <Route exact path={routes.newExpenseScenarioPath}>
        <ExpenseScenarioFormPage />
      </Route>
      <Route exact path={routes.expenseScenarioDetailPath}>
        <ExpenseScenarioFormPage />
      </Route>
      <Route exact path={routes.expenseScenariosPath}>
        <ExpenseScenariosPage />
      </Route>
    </Switch>
  );
}
